import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const CHART_HEIGHT = 400;
const CHART_PADDING = { top: 18, right: 20, bottom: 44, left: 58 };

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatMetricValue(value, metric) {
  if (metric === 'cost') {
    return `$${(Number(value || 0) || 0).toFixed(6)}`;
  }

  return (Number(value || 0) || 0).toLocaleString();
}

function getBucketValue(bucket, key) {
  if (key === 'total') {
    return Number(bucket?.total || 0) || 0;
  }

  return Number(bucket?.series?.[key] || 0) || 0;
}

function buildPath(points = []) {
  if (points.length === 0) {
    return '';
  }

  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
}

function useChartWidth() {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      const nextWidth = node.getBoundingClientRect().width;
      setWidth(nextWidth > 0 ? nextWidth : 0);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(node);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    return undefined;
  }, []);

  return { containerRef, width: Math.max(width, 320) };
}

export function InferencePlotChart({
  chartData = [],
  seriesKeys = [],
  metric = 'records',
  metricLabel = 'Record Count',
  getSeriesColor,
  isLoading = false,
  xDomainStartMs = null,
  xDomainEndExclusiveMs = null,
  axisStartLabel = '',
  axisEndLabel = '',
}) {
  const { containerRef, width } = useChartWidth();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });

  const activeSeriesKeys = useMemo(
    () => (seriesKeys && seriesKeys.length > 0 ? seriesKeys : ['total']),
    [seriesKeys]
  );

  const geometry = useMemo(() => {
    const safeData = Array.isArray(chartData) ? [...chartData] : [];
    safeData.sort((a, b) => (a?.bucketStart?.getTime?.() || 0) - (b?.bucketStart?.getTime?.() || 0));

    const innerWidth = Math.max(1, width - CHART_PADDING.left - CHART_PADDING.right);
    const innerHeight = Math.max(1, CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom);

    const fallbackStart =
      safeData[0]?.bucketStart instanceof Date ? safeData[0].bucketStart.getTime() : 0;
    const fallbackEnd =
      safeData[safeData.length - 1]?.bucketStart instanceof Date
        ? safeData[safeData.length - 1].bucketStart.getTime() + 1
        : fallbackStart + 1;

    const domainStart = Number.isFinite(xDomainStartMs) ? xDomainStartMs : fallbackStart;
    const domainEndExclusive = Number.isFinite(xDomainEndExclusiveMs)
      ? xDomainEndExclusiveMs
      : fallbackEnd;
    const domainDuration = Math.max(1, domainEndExclusive - domainStart);

    const xForMs = (ms) =>
      CHART_PADDING.left + ((ms - domainStart) / domainDuration) * innerWidth;

    let maxValue = 0;
    for (const bucket of safeData) {
      for (const key of activeSeriesKeys) {
        maxValue = Math.max(maxValue, getBucketValue(bucket, key));
      }
    }

    const yMax = maxValue > 0 ? maxValue : 1;
    const yForValue = (value) =>
      CHART_PADDING.top + innerHeight - (Math.max(0, value) / yMax) * innerHeight;

    const series = activeSeriesKeys.map((key) => ({
      key,
      color: getSeriesColor(key),
      path: buildPath(
        safeData.map((bucket) => ({
          x: xForMs(bucket.bucketStart.getTime()),
          y: yForValue(getBucketValue(bucket, key)),
        }))
      ),
    }));

    const hoverBuckets = safeData
      .map((bucket) => ({
        bucket,
        x: xForMs(bucket.bucketStart.getTime()),
      }))
      .sort((a, b) => a.x - b.x);

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
      const value = yMax * ratio;
      return {
        key: ratio,
        label: formatMetricValue(value, metric),
        y: yForValue(value),
      };
    });

    return {
      xForMs,
      yForValue,
      yTicks,
      series,
      hoverBuckets,
    };
  }, [activeSeriesKeys, chartData, getSeriesColor, metric, width, xDomainEndExclusiveMs, xDomainStartMs]);

  const hoveredBucket = hoverIndex != null ? geometry.hoverBuckets[hoverIndex]?.bucket : null;

  const tooltipSeriesKeys = useMemo(() => {
    if (!hoveredBucket) {
      return [];
    }
    return activeSeriesKeys
      .map((key) => ({
        key,
        value: Number(getBucketValue(hoveredBucket, key)) || 0,
      }))
      .filter(({ value }) => value > 0)
      .sort((a, b) => b.value - a.value)
      .map(({ key }) => key);
  }, [activeSeriesKeys, hoveredBucket]);

  const handleMouseMove = (event) => {
    if (!containerRef.current || geometry.hoverBuckets.length === 0 || typeof window === 'undefined') {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const localX = clamp(event.clientX - rect.left, CHART_PADDING.left, width - CHART_PADDING.right);

    let nearestIndex = 0;
    let nearestDistance = Math.abs(geometry.hoverBuckets[0].x - localX);
    for (let index = 1; index < geometry.hoverBuckets.length; index += 1) {
      const distance = Math.abs(geometry.hoverBuckets[index].x - localX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }

    setHoverIndex(nearestIndex);
    setTooltipPosition({
      left: clamp(rect.left + geometry.hoverBuckets[nearestIndex].x + 14, 12, window.innerWidth - 280),
      top: rect.top + 18,
    });
  };

  if (isLoading) {
    return <div className="admin-inferences-chart-empty">Loading chart...</div>;
  }

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return <div className="admin-inferences-chart-empty">No inference data for the current result set.</div>;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="admin-inferences-chart-wrapper"
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg
          width={width}
          height={CHART_HEIGHT}
          className="admin-inferences-chart-svg"
          onMouseMove={handleMouseMove}
        >
          {geometry.yTicks.map((tick) => (
            <g key={tick.key}>
              <line
                x1={CHART_PADDING.left}
                y1={tick.y}
                x2={width - CHART_PADDING.right}
                y2={tick.y}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
              />
              <text
                x={CHART_PADDING.left - 10}
                y={tick.y + 4}
                textAnchor="end"
                fill="#9ca3af"
                fontSize="11"
              >
                {tick.label}
              </text>
            </g>
          ))}

          <line
            x1={CHART_PADDING.left}
            y1={CHART_HEIGHT - CHART_PADDING.bottom}
            x2={width - CHART_PADDING.right}
            y2={CHART_HEIGHT - CHART_PADDING.bottom}
            stroke="rgba(255,255,255,0.14)"
            strokeWidth="1"
          />

          {geometry.series.map((series) => (
            <path
              key={series.key}
              d={series.path}
              fill="none"
              stroke={series.color}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {hoveredBucket && (
            <line
              x1={geometry.xForMs(hoveredBucket.bucketStart.getTime())}
              y1={CHART_PADDING.top}
              x2={geometry.xForMs(hoveredBucket.bucketStart.getTime())}
              y2={CHART_HEIGHT - CHART_PADDING.bottom}
              stroke="rgba(255,255,255,0.35)"
              strokeDasharray="4 4"
              strokeWidth="1"
              pointerEvents="none"
            />
          )}

          {hoveredBucket &&
            tooltipSeriesKeys.map((key) => (
              <circle
                key={key}
                cx={geometry.xForMs(hoveredBucket.bucketStart.getTime())}
                cy={geometry.yForValue(getBucketValue(hoveredBucket, key))}
                r="4.5"
                fill={getSeriesColor(key)}
                stroke="#111827"
                strokeWidth="1.5"
              />
            ))}

          <text
            x={CHART_PADDING.left}
            y={CHART_HEIGHT - 12}
            textAnchor="start"
            fill="#9ca3af"
            fontSize="11"
          >
            {axisStartLabel}
          </text>
          <text
            x={width - CHART_PADDING.right}
            y={CHART_HEIGHT - 12}
            textAnchor="end"
            fill="#9ca3af"
            fontSize="11"
          >
            {axisEndLabel}
          </text>
        </svg>

        {activeSeriesKeys.length > 1 && (
          <div className="admin-inferences-chart-legend">
            {activeSeriesKeys.map((key) => (
              <div className="admin-inferences-chart-legend-item" key={key}>
                <span
                  className="admin-inferences-chart-legend-dot"
                  style={{ backgroundColor: getSeriesColor(key) }}
                />
                <span>{key === 'total' ? metricLabel : key}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {hoveredBucket &&
        ReactDOM.createPortal(
          <div
            className="stripe-chart-tooltip admin-inferences-chart-hover-tooltip"
            style={{
              position: 'fixed',
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            <div className="stripe-chart-tooltip-title">{hoveredBucket.label}</div>
            <div className="admin-inferences-chart-tooltip-list">
              {tooltipSeriesKeys.map((key) => (
                <div className="admin-inferences-chart-tooltip-row" key={key}>
                  <span
                    className="admin-inferences-chart-tooltip-dot"
                    style={{ backgroundColor: getSeriesColor(key) }}
                  />
                  <span className="admin-inferences-chart-tooltip-name">
                    {key === 'total' ? metricLabel : key}
                  </span>
                  <span className="admin-inferences-chart-tooltip-value">
                    {formatMetricValue(getBucketValue(hoveredBucket, key), metric)}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
